/**
 * Topic-aware SVG illustrations for blog posts.
 * Each tag gets a recognizable scene — readers instantly know the topic.
 * Deterministic: same tags + title always produce the same visual.
 */

interface Props {
  tags: string[];
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}

/* ── Palette per category ───────────────────────────────────── */
const PALETTES: Record<string, { bg: string; a: string; b: string; c: string }> = {
  'FinOps':             { bg: '#07281D', a: '#E8B84C', b: '#4E7D5E', c: '#0F3D2E' },
  'Cost Optimization':  { bg: '#071f17', a: '#4E7D5E', b: '#E8B84C', c: '#1a6b42' },
  'AWS':                { bg: '#1a0f00', a: '#FF9900', b: '#ff6b2b', c: '#3d2000' },
  'EC2':                { bg: '#001a1f', a: '#06b6d4', b: '#0e7490', c: '#164e63' },
  'S3':                 { bg: '#1a0a2e', a: '#a855f7', b: '#7c3aed', c: '#3b0764' },
  'RDS':                { bg: '#1a0000', a: '#ef4444', b: '#dc2626', c: '#450a0a' },
  'Reserved Instances': { bg: '#001a0a', a: '#22c55e', b: '#16a34a', c: '#052e16' },
  'Savings Plans':      { bg: '#1a1000', a: '#eab308', b: '#ca8a04', c: '#422006' },
  'Kubernetes':         { bg: '#00001a', a: '#3b82f6', b: '#1d4ed8', c: '#1e1b4b' },
  'Serverless':         { bg: '#1a0800', a: '#f97316', b: '#ea580c', c: '#431407' },
  'EKS':                { bg: '#001a12', a: '#10b981', b: '#059669', c: '#022c22' },
  'Lambda':             { bg: '#0f0a1a', a: '#8b5cf6', b: '#7c3aed', c: '#2e1065' },
};
const DEFAULT_PALETTE = { bg: '#07281D', a: '#4E7D5E', b: '#E8B84C', c: '#0F3D2E' };

function getPalette(tags: string[]) {
  for (const tag of tags) if (PALETTES[tag]) return PALETTES[tag];
  return DEFAULT_PALETTE;
}

/* ── Scene components — recognizable topic illustrations ──── */

/** FinOps: descending cost chart with dollar sign */
function SceneCostChart({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Grid lines */}
      {[30, 50, 70, 90].map(y => (
        <line key={y} x1="20" y1={y} x2="170" y2={y} stroke={c} strokeWidth="0.5" opacity="0.3" />
      ))}
      {/* Descending bars — showing cost going DOWN */}
      {[
        { x: 30, h: 72, o: 0.5 },
        { x: 52, h: 60, o: 0.55 },
        { x: 74, h: 45, o: 0.65 },
        { x: 96, h: 35, o: 0.75 },
        { x: 118, h: 22, o: 0.9 },
      ].map((bar, i) => (
        <rect key={i} x={bar.x} y={100 - bar.h} width="16" height={bar.h} rx="2" fill={i === 4 ? a : b} opacity={bar.o} />
      ))}
      {/* Trend line */}
      <path d="M 38 28 L 60 40 L 82 55 L 104 65 L 126 78" fill="none" stroke={a} strokeWidth="2" strokeLinecap="round" opacity="0.8" />
      {/* Arrow at end */}
      <path d="M 123 75 L 129 81 L 121 80" fill={a} opacity="0.8" />
      {/* Dollar sign */}
      <text x="152" y="45" fontSize="24" fontWeight="bold" fill={a} opacity="0.25" fontFamily="system-ui">$</text>
    </>
  );
}

/** AWS general: cloud shape with services radiating */
function SceneCloud({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Main cloud */}
      <path d="M 55 72 C 55 55, 70 45, 85 50 C 90 38, 110 35, 118 48 C 130 42, 145 50, 140 62 C 150 65, 150 78, 138 80 L 60 80 C 48 80, 48 72, 55 72 Z"
        fill={c} opacity="0.5" />
      <path d="M 58 70 C 58 55, 72 47, 87 52 C 92 40, 108 37, 116 48 C 127 43, 140 50, 136 60 C 146 63, 146 75, 135 77 L 63 77 C 52 77, 52 70, 58 70 Z"
        fill="none" stroke={a} strokeWidth="1.5" opacity="0.7" />
      {/* Service dots orbiting */}
      {[
        { cx: 45, cy: 55, r: 4 },
        { cx: 155, cy: 48, r: 3.5 },
        { cx: 70, cy: 35, r: 3 },
        { cx: 130, cy: 30, r: 3.5 },
        { cx: 50, cy: 90, r: 3 },
        { cx: 148, cy: 85, r: 3 },
      ].map((d, i) => (
        <g key={i}>
          <line x1={97} y1={60} x2={d.cx} y2={d.cy} stroke={b} strokeWidth="0.5" opacity="0.2" strokeDasharray="3 3" />
          <circle cx={d.cx} cy={d.cy} r={d.r} fill={i % 2 === 0 ? a : b} opacity={0.7} />
        </g>
      ))}
    </>
  );
}

/** EC2: server rack / compute instances */
function SceneServers({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Three server units stacked */}
      {[0, 1, 2].map(i => {
        const y = 25 + i * 28;
        const active = i === 1;
        return (
          <g key={i}>
            <rect x="50" y={y} width="90" height="22" rx="3" fill={c} opacity={active ? 0.6 : 0.35} />
            <rect x="50" y={y} width="90" height="22" rx="3" fill="none" stroke={active ? a : b} strokeWidth="1" opacity={active ? 0.8 : 0.3} />
            {/* Status LEDs */}
            <circle cx={62} cy={y + 11} r="2.5" fill={active ? a : b} opacity={active ? 0.9 : 0.3} />
            <circle cx={72} cy={y + 11} r="2.5" fill={active ? '#22c55e' : b} opacity={active ? 0.7 : 0.2} />
            {/* Drive bays */}
            {[0, 1, 2, 3].map(j => (
              <rect key={j} x={90 + j * 12} y={y + 5} width="8" height="12" rx="1" fill={b} opacity={0.15 + j * 0.05} />
            ))}
          </g>
        );
      })}
      {/* Utilization meter on side */}
      <rect x="150" y="30" width="6" height="60" rx="3" fill={c} opacity="0.4" />
      <rect x="150" y="55" width="6" height="35" rx="3" fill={a} opacity="0.6" />
    </>
  );
}

/** S3: storage buckets / file system */
function SceneStorage({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Bucket shape — cylinder */}
      {[0, 1, 2].map(i => {
        const x = 35 + i * 42;
        const h = 40 + (2 - i) * 10;
        const y = 95 - h;
        return (
          <g key={i}>
            {/* Body */}
            <rect x={x} y={y + 6} width="32" height={h - 6} rx="2" fill={c} opacity={0.4 + i * 0.1} />
            {/* Top ellipse */}
            <ellipse cx={x + 16} cy={y + 6} rx="16" ry="6" fill={i === 0 ? a : b} opacity={0.5 + i * 0.1} />
            {/* Fill level */}
            <rect x={x + 4} y={y + h * 0.4} width="24" height={h * 0.5} rx="1" fill={a} opacity={0.15 + i * 0.08} />
            {/* Label lines */}
            <rect x={x + 8} y={y + h * 0.25} width="16" height="1.5" rx="1" fill={b} opacity="0.3" />
          </g>
        );
      })}
      {/* Arrows flowing between buckets */}
      <path d="M 70 70 Q 80 62, 82 70" fill="none" stroke={a} strokeWidth="1" opacity="0.4" />
      <path d="M 112 65 Q 122 57, 124 65" fill="none" stroke={a} strokeWidth="1" opacity="0.4" />
    </>
  );
}

/** RDS: database cylinder with query lines */
function SceneDatabase({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Main DB cylinder */}
      <ellipse cx="90" cy="35" rx="35" ry="10" fill={c} opacity="0.5" />
      <rect x="55" y="35" width="70" height="50" fill={c} opacity="0.35" />
      <ellipse cx="90" cy="85" rx="35" ry="10" fill={c} opacity="0.5" />
      <ellipse cx="90" cy="35" rx="35" ry="10" fill="none" stroke={a} strokeWidth="1.5" opacity="0.6" />
      <ellipse cx="90" cy="55" rx="35" ry="8" fill="none" stroke={b} strokeWidth="0.5" opacity="0.25" />
      <ellipse cx="90" cy="70" rx="35" ry="8" fill="none" stroke={b} strokeWidth="0.5" opacity="0.2" />
      {/* Side edges */}
      <line x1="55" y1="35" x2="55" y2="85" stroke={a} strokeWidth="1.5" opacity="0.4" />
      <line x1="125" y1="35" x2="125" y2="85" stroke={a} strokeWidth="1.5" opacity="0.4" />
      {/* Data rows inside */}
      {[45, 55, 65, 75].map((y, i) => (
        <g key={i}>
          <rect x={65} y={y} width={50 - i * 4} height="2" rx="1" fill={a} opacity={0.15 + i * 0.05} />
        </g>
      ))}
      {/* Read replica - smaller cylinder to the right */}
      <ellipse cx="148" cy="60" rx="14" ry="5" fill={c} opacity="0.3" />
      <rect x="134" y="60" width="28" height="22" fill={c} opacity="0.2" />
      <ellipse cx="148" cy="82" rx="14" ry="5" fill={c} opacity="0.3" />
      <path d="M 128 50 Q 138 45 140 55" fill="none" stroke={a} strokeWidth="1" opacity="0.3" strokeDasharray="3 2" />
    </>
  );
}

/** Reserved Instances / Savings Plans: piggy bank / savings */
function SceneSavings({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Coin stack */}
      {[0, 1, 2, 3, 4].map(i => {
        const y = 80 - i * 10;
        return (
          <g key={i}>
            <ellipse cx="70" cy={y} rx="20" ry="5" fill={c} opacity={0.5} />
            <rect x="50" y={y} width="40" height="8" fill={c} opacity={0.35} />
            <ellipse cx="70" cy={y + 8} rx="20" ry="5" fill={c} opacity={0.3} />
            <ellipse cx="70" cy={y} rx="20" ry="5" fill="none" stroke={i < 3 ? b : a} strokeWidth="1" opacity={0.3 + i * 0.12} />
          </g>
        );
      })}
      {/* Dollar sign on top coin */}
      <text x="65" y="38" fontSize="10" fontWeight="bold" fill={a} opacity="0.7" fontFamily="system-ui">$</text>
      {/* Upward arrow — savings growing */}
      <line x1="120" y1="85" x2="120" y2="30" stroke={a} strokeWidth="2" opacity="0.5" />
      <path d="M 114 38 L 120 28 L 126 38" fill="none" stroke={a} strokeWidth="2" opacity="0.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Percentage badge */}
      <rect x="108" y="55" width="26" height="14" rx="4" fill={a} opacity="0.15" />
      <text x="113" y="65" fontSize="8" fontWeight="bold" fill={a} opacity="0.6" fontFamily="system-ui">%</text>
    </>
  );
}

/** Kubernetes / EKS: container pods connected */
function SceneContainers({ a, b, c }: { a: string; b: string; c: string }) {
  const pods = [
    { x: 55, y: 30, w: 28, h: 24 },
    { x: 100, y: 30, w: 28, h: 24 },
    { x: 55, y: 68, w: 28, h: 24 },
    { x: 100, y: 68, w: 28, h: 24 },
  ];
  return (
    <>
      {/* Connections between pods */}
      <line x1="83" y1="42" x2="100" y2="42" stroke={b} strokeWidth="1" opacity="0.3" />
      <line x1="83" y1="80" x2="100" y2="80" stroke={b} strokeWidth="1" opacity="0.3" />
      <line x1="69" y1="54" x2="69" y2="68" stroke={b} strokeWidth="1" opacity="0.3" />
      <line x1="114" y1="54" x2="114" y2="68" stroke={b} strokeWidth="1" opacity="0.3" />
      {/* Pods */}
      {pods.map((p, i) => (
        <g key={i}>
          <rect x={p.x} y={p.y} width={p.w} height={p.h} rx="4" fill={c} opacity={0.45} />
          <rect x={p.x} y={p.y} width={p.w} height={p.h} rx="4" fill="none" stroke={i < 2 ? a : b} strokeWidth="1" opacity="0.5" />
          {/* Container icon inside */}
          <rect x={p.x + 6} y={p.y + 5} width="7" height="14" rx="1" fill={a} opacity={0.3} />
          <rect x={p.x + 15} y={p.y + 5} width="7" height="14" rx="1" fill={b} opacity={0.25} />
        </g>
      ))}
      {/* Orchestrator node */}
      <circle cx="145" cy="60" r="12" fill={c} opacity="0.4" />
      <circle cx="145" cy="60" r="12" fill="none" stroke={a} strokeWidth="1" opacity="0.4" />
      {/* Helm wheel icon */}
      {[0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        return (
          <line key={i} x1={145} y1={60} x2={145 + 8 * Math.cos(rad)} y2={60 + 8 * Math.sin(rad)}
            stroke={a} strokeWidth="1.5" opacity="0.35" strokeLinecap="round" />
        );
      })}
      <circle cx="145" cy="60" r="3" fill={a} opacity="0.6" />
    </>
  );
}

/** Lambda / Serverless: event-driven flow */
function SceneServerless({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Event source — zigzag/lightning */}
      <path d="M 35 45 L 45 35 L 40 50 L 50 40" fill="none" stroke={a} strokeWidth="2" opacity="0.7" strokeLinecap="round" strokeLinejoin="round" />
      {/* Arrow to function */}
      <line x1="55" y1="45" x2="72" y2="55" stroke={b} strokeWidth="1" opacity="0.4" />
      <path d="M 69 52 L 75 57 L 68 57" fill={b} opacity="0.4" />
      {/* Lambda function box */}
      <rect x="75" y="40" width="40" height="30" rx="5" fill={c} opacity="0.5" />
      <rect x="75" y="40" width="40" height="30" rx="5" fill="none" stroke={a} strokeWidth="1.2" opacity="0.6" />
      {/* Lambda symbol */}
      <text x="86" y="62" fontSize="16" fontWeight="300" fill={a} opacity="0.7" fontFamily="Georgia, serif">λ</text>
      {/* Arrow to output */}
      <line x1="118" y1="55" x2="135" y2="50" stroke={b} strokeWidth="1" opacity="0.4" />
      <path d="M 132 47 L 138 50 L 132 53" fill={b} opacity="0.4" />
      {/* Output — DB or API */}
      <circle cx="148" cy="48" r="10" fill={c} opacity="0.4" />
      <circle cx="148" cy="48" r="10" fill="none" stroke={b} strokeWidth="1" opacity="0.4" />
      {/* Invocation count pulses */}
      {[75, 82, 89].map((y, i) => (
        <rect key={i} x={60 + i * 22} y={y} width={30 - i * 4} height="2" rx="1" fill={a} opacity={0.12 + i * 0.06} />
      ))}
    </>
  );
}

/** Network: VPC / data transfer paths */
function SceneNetwork({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* VPC boundary */}
      <rect x="35" y="25" width="120" height="70" rx="8" fill="none" stroke={c} strokeWidth="1.5" opacity="0.5" strokeDasharray="6 3" />
      {/* Subnets */}
      <rect x="45" y="35" width="40" height="25" rx="3" fill={c} opacity="0.35" />
      <rect x="105" y="35" width="40" height="25" rx="3" fill={c} opacity="0.35" />
      <rect x="75" y="68" width="40" height="20" rx="3" fill={c} opacity="0.25" />
      {/* Data flow arrows */}
      <path d="M 85 47 L 105 47" fill="none" stroke={a} strokeWidth="1.5" opacity="0.5" markerEnd="" />
      <path d="M 102 44 L 107 47 L 102 50" fill={a} opacity="0.5" />
      <path d="M 95 60 L 95 68" fill="none" stroke={b} strokeWidth="1" opacity="0.4" />
      {/* External — globe icon */}
      <circle cx="170" cy="50" r="10" fill="none" stroke={a} strokeWidth="1" opacity="0.35" />
      <ellipse cx="170" cy="50" rx="5" ry="10" fill="none" stroke={a} strokeWidth="0.5" opacity="0.25" />
      <line x1="160" y1="50" x2="180" y2="50" stroke={a} strokeWidth="0.5" opacity="0.25" />
      {/* Arrow out to globe */}
      <path d="M 155 47 Q 162 40, 160 47" fill="none" stroke={a} strokeWidth="1" opacity="0.4" />
      {/* Node dots */}
      <circle cx="60" cy="47" r="3" fill={a} opacity="0.7" />
      <circle cx="120" cy="47" r="3" fill={b} opacity="0.6" />
      <circle cx="95" cy="78" r="3" fill={a} opacity="0.5" />
    </>
  );
}

/** General optimization: wrench + gauge */
function SceneOptimize({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Gauge arc */}
      <path d="M 55 85 A 40 40 0 0 1 135 85" fill="none" stroke={c} strokeWidth="8" opacity="0.35" strokeLinecap="round" />
      {/* Gauge fill — showing "optimized" (right side lit) */}
      <path d="M 90 45 A 40 40 0 0 1 135 85" fill="none" stroke={a} strokeWidth="8" opacity="0.6" strokeLinecap="round" />
      {/* Needle */}
      <line x1="95" y1="80" x2="120" y2="52" stroke={a} strokeWidth="2" opacity="0.8" strokeLinecap="round" />
      <circle cx="95" cy="80" r="4" fill={a} opacity="0.9" />
      {/* Zone labels */}
      <circle cx="62" cy="68" r="2" fill={b} opacity="0.4" />
      <circle cx="128" cy="68" r="2" fill={a} opacity="0.6" />
      {/* Settings gear (small) */}
      <circle cx="148" cy="40" r="8" fill="none" stroke={b} strokeWidth="1.5" opacity="0.3" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        return (
          <line key={i} x1={148 + 6 * Math.cos(rad)} y1={40 + 6 * Math.sin(rad)}
            x2={148 + 10 * Math.cos(rad)} y2={40 + 10 * Math.sin(rad)}
            stroke={b} strokeWidth="1.5" opacity="0.25" strokeLinecap="round" />
        );
      })}
      <circle cx="148" cy="40" r="3" fill={b} opacity="0.3" />
    </>
  );
}

/** Monitoring / Anomaly detection: eye + alert */
function SceneMonitoring({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Eye shape */}
      <path d="M 40 60 Q 90 25, 140 60 Q 90 95, 40 60 Z" fill={c} opacity="0.3" />
      <path d="M 40 60 Q 90 25, 140 60 Q 90 95, 40 60 Z" fill="none" stroke={a} strokeWidth="1.5" opacity="0.5" />
      <circle cx="90" cy="60" r="15" fill={c} opacity="0.5" />
      <circle cx="90" cy="60" r="15" fill="none" stroke={b} strokeWidth="1" opacity="0.4" />
      <circle cx="90" cy="60" r="7" fill={a} opacity="0.7" />
      <circle cx="93" cy="57" r="2.5" fill="white" opacity="0.4" />
      {/* Scanning lines radiating */}
      {[-25, -15, 15, 25].map((offset, i) => (
        <line key={i} x1={90 + offset * 2.2} y1={60 - 30 + Math.abs(offset) * 0.3} x2={90 + offset * 2.2} y2={60 + 30 - Math.abs(offset) * 0.3}
          stroke={b} strokeWidth="0.5" opacity="0.1" />
      ))}
      {/* Alert badge */}
      <circle cx="148" cy="35" r="7" fill={a} opacity="0.3" />
      <text x="145" y="39" fontSize="10" fontWeight="bold" fill={a} opacity="0.7" fontFamily="system-ui">!</text>
    </>
  );
}

/** Governance / Tags / Budgets: dashboard with tags */
function SceneGovernance({ a, b, c }: { a: string; b: string; c: string }) {
  return (
    <>
      {/* Dashboard frame */}
      <rect x="35" y="25" width="120" height="70" rx="5" fill={c} opacity="0.3" />
      <rect x="35" y="25" width="120" height="70" rx="5" fill="none" stroke={b} strokeWidth="1" opacity="0.25" />
      {/* Top bar */}
      <rect x="35" y="25" width="120" height="12" rx="5" fill={c} opacity="0.3" />
      <circle cx="46" cy="31" r="2" fill={a} opacity="0.6" />
      <circle cx="54" cy="31" r="2" fill={b} opacity="0.4" />
      <circle cx="62" cy="31" r="2" fill={b} opacity="0.3" />
      {/* Tag pills inside */}
      {[
        { x: 45, y: 44, w: 30 },
        { x: 80, y: 44, w: 24 },
        { x: 45, y: 58, w: 36 },
        { x: 86, y: 58, w: 28 },
        { x: 45, y: 72, w: 22 },
        { x: 72, y: 72, w: 32 },
        { x: 108, y: 72, w: 20 },
      ].map((t, i) => (
        <rect key={i} x={t.x} y={t.y} width={t.w} height="9" rx="4.5" fill={i < 2 ? a : b} opacity={0.15 + (i % 3) * 0.08} />
      ))}
      {/* Pie chart mini */}
      <circle cx="138" cy="48" r="8" fill={c} opacity="0.4" />
      <path d={`M 138 48 L 138 40 A 8 8 0 0 1 ${138 + 8 * Math.cos(-Math.PI / 6)} ${48 - 8 * Math.sin(Math.PI / 6)} Z`}
        fill={a} opacity="0.5" />
    </>
  );
}

/* ── Scene-to-tag mapping ────────────────────────────────── */

const TAG_SCENES: Record<string, React.FC<{ a: string; b: string; c: string }>> = {
  'FinOps':             SceneCostChart,
  'Cost Optimization':  SceneOptimize,
  'AWS':                SceneCloud,
  'EC2':                SceneServers,
  'S3':                 SceneStorage,
  'RDS':                SceneDatabase,
  'Reserved Instances': SceneSavings,
  'Savings Plans':      SceneSavings,
  'Kubernetes':         SceneContainers,
  'Serverless':         SceneServerless,
  'EKS':                SceneContainers,
  'Lambda':             SceneServerless,
};

/* Fallback scenes for title keyword matching */
const KEYWORD_SCENES: [RegExp, React.FC<{ a: string; b: string; c: string }>][] = [
  [/network|vpc|transfer|nat|egress|cdn|cloudfront/i, SceneNetwork],
  [/budget|alert|anomal|monitor|detect/i, SceneMonitoring],
  [/tag|govern|allocat|account|compliance/i, SceneGovernance],
  [/server|ec2|instance|compute|graviton|rightsiz/i, SceneServers],
  [/storage|s3|ebs|efs|glacier|bucket/i, SceneStorage],
  [/database|rds|aurora|dynamo|redis|elastic.?cache/i, SceneDatabase],
  [/container|kubernetes|k8s|eks|fargate|docker/i, SceneContainers],
  [/lambda|serverless|function|step.?function|api.?gateway/i, SceneServerless],
  [/reserv|saving|discount|commit|plan/i, SceneSavings],
  [/cost|optimi|expense|bill|spend|pric|unit.?econ|finops/i, SceneCostChart],
];

const ALL_SCENES = [SceneCostChart, SceneCloud, SceneServers, SceneStorage, SceneDatabase, SceneSavings, SceneContainers, SceneServerless, SceneNetwork, SceneMonitoring, SceneOptimize, SceneGovernance];

// Broad tags that almost every post has — lowest priority for scene selection
const BROAD_TAGS = new Set(['AWS', 'FinOps', 'Cost Optimization']);

function pickScene(tags: string[], title: string) {
  // 1. Specific tag match (skip broad tags like AWS that appear on everything)
  for (const tag of tags) {
    if (!BROAD_TAGS.has(tag) && TAG_SCENES[tag]) return TAG_SCENES[tag];
  }
  // 2. Title keyword match — most discriminating for topic
  for (const [re, Scene] of KEYWORD_SCENES) {
    if (re.test(title)) return Scene;
  }
  // 3. Broad tag match (better than random)
  for (const tag of tags) {
    if (BROAD_TAGS.has(tag) && TAG_SCENES[tag]) return TAG_SCENES[tag];
  }
  // 4. Deterministic fallback
  let hash = 0;
  const key = (tags[0] ?? '') + title;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return ALL_SCENES[hash % ALL_SCENES.length];
}

/* ── Main component ──────────────────────────────────────── */

export default function PostIllustration({ tags, title = '', className = '', style }: Props) {
  const pal = getPalette(tags);
  const Scene = pickScene(tags, title);
  const glowId = `glow-${(tags[0] ?? '').replace(/\s/g, '')}-${title.slice(0, 8).replace(/\s/g, '')}`;

  return (
    <svg
      viewBox="0 0 420 120"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ display: 'block', ...style }}
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id={glowId} cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={pal.a} stopOpacity="0.12" />
          <stop offset="70%" stopColor={pal.bg} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="420" height="120" fill={pal.bg} />
      <rect width="420" height="120" fill={`url(#${glowId})`} />
      {/* Scene centered in the wide canvas */}
      <g transform="translate(120, 5)">
        <Scene a={pal.a} b={pal.b} c={pal.c} />
      </g>
    </svg>
  );
}
